import { v4 as uuidv4 } from 'uuid';
import { FixedAsset, DepreciationScheduleLine } from './fixed-assets.schema';

export class FixedAssetsService {
  registerAsset(data: Omit<FixedAsset, 'id'>): FixedAsset {
    return {
      id: uuidv4(),
      ...data,
    };
  }

  calculateStraightLineDepreciation(asset: FixedAsset): DepreciationScheduleLine[] {
    const { purchasePrice, salvageValue, usefulLifeMonths, purchaseDate } = asset;
    const depreciableAmount = purchasePrice - salvageValue;
    
    if (usefulLifeMonths <= 0) return [];
    
    const monthlyDepreciation = depreciableAmount / usefulLifeMonths;
    const schedule: DepreciationScheduleLine[] = [];
    
    let currentDate = new Date(purchaseDate);
    
    for (let i = 1; i <= usefulLifeMonths; i++) {
      currentDate = new Date(currentDate.setMonth(currentDate.getMonth() + 1));
      schedule.push({
        date: currentDate.toISOString(),
        amount: monthlyDepreciation,
      });
    }
    
    return schedule;
  }

  calculateDecliningBalance(asset: FixedAsset, rate: number): DepreciationScheduleLine[] {
    const { purchasePrice, salvageValue, usefulLifeMonths, purchaseDate } = asset;
    const schedule: DepreciationScheduleLine[] = [];
    
    let currentBookValue = purchasePrice;
    let currentDate = new Date(purchaseDate);
    
    for (let i = 1; i <= usefulLifeMonths; i++) {
      if (currentBookValue <= salvageValue) {
        break;
      }

      currentDate = new Date(currentDate.setMonth(currentDate.getMonth() + 1));
      
      let annualDepreciationRate = rate / (usefulLifeMonths / 12);
      let annualDepreciation = currentBookValue * annualDepreciationRate;
      let monthlyDepreciation = annualDepreciation / 12;

      if (currentBookValue - monthlyDepreciation < salvageValue) {
        monthlyDepreciation = currentBookValue - salvageValue;
      }

      schedule.push({
        date: currentDate.toISOString(),
        amount: monthlyDepreciation,
      });

      currentBookValue -= monthlyDepreciation;
      
      if (currentBookValue <= salvageValue && i < usefulLifeMonths) {
         break;
      }
    }
    
    return schedule;
  }

  postMonthlyDepreciation(asset: FixedAsset, monthDate: string): any {
    const schedule = asset.depreciationMethod === 'Straight-Line' 
      ? this.calculateStraightLineDepreciation(asset)
      : this.calculateDecliningBalance(asset, 2);

    const targetMonth = new Date(monthDate).toISOString().slice(0, 7);
    
    const line = schedule.find(l => l.date.startsWith(targetMonth));
    if (!line) {
        throw new Error("No depreciation found for the given month.");
    }
    
    const payload = {
      tenantId: asset.tenantId,
      date: line.date,
      description: `Monthly depreciation for ${asset.name}`,
      lines: [
        {
          accountId: asset.depreciationExpenseAccountId,
          type: 'debit',
          amount: line.amount,
        },
        {
          accountId: asset.accumulatedDepreciationAccountId,
          type: 'credit',
          amount: line.amount,
        }
      ]
    };
    
    return payload;
  }
}
